import { Loading } from '@nike/frame-component-library';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';

import { taxGet } from '../../../utils/service-calls/store-data-validation';
import { ButtonBlack } from '../../reusable';

const TAX_COLUMNS = [
  { key: 'taxType', label: 'Tax Type' },
  { key: 'subTaxType', label: 'Sub Tax Type' },
  { key: 'taxRate', label: 'Tax Rate' },
  { key: 'taxId', label: 'Tax ID' },
  { key: 'taxJurisdictionId', label: 'Tax Jurisdiction ID' },
  { key: 'inclusive', label: 'Inclusive' },
  { key: 'interstate', label: 'Interstate' },
  { key: 'priceCondition', label: 'Price Condition' },
  { key: 'exemptAmount', label: 'Exempt Amount' },
  { key: 'notBefore', label: 'Not Before' },
  { key: 'notAfter', label: 'Not After' },
  { key: 'roundRule', label: 'Round Rule' },
];

const formatBoolean = (value) => {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return '-';
};

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
};

const getPriceCondition = (rule) => {
  if (rule.priceGreater !== undefined) return `Price > ${rule.priceGreater}`;
  if (rule.priceGreaterThanEqual !== undefined) return `Price >= ${rule.priceGreaterThanEqual}`;
  if (rule.priceLess !== undefined) return `Price < ${rule.priceLess}`;
  if (rule.priceLessThanEqual !== undefined) return `Price <= ${rule.priceLessThanEqual}`;
  return 'Any';
};

const getRoundRuleDisplay = (roundRule) => {
  if (!roundRule) return '-';
  const parts = [];
  if (roundRule.rule) parts.push(`Rule : ${roundRule.rule}`);
  if (roundRule.minimumInterval !== undefined) parts.push(`Minimum-Interval : ${roundRule.minimumInterval}`);
  return parts.join(' | ') || '-';
};

const normaliseTaxes = (body) => {
  const objects = body?.objects || [];
  return objects.map((rule, index) => ({
    ...rule,
    priceCondition: getPriceCondition(rule),
    roundRuleDisplay: getRoundRuleDisplay(rule.roundRule),
    rowId: `${rule.taxId || 'tax'}-${rule.subTaxType || 'na'}-${index}`,
    taxJurisdictionId: rule.taxJurisdictionId || rule.taxJurisdictionID,
  }));
};

const renderTaxCell = (row, col) => {
  if (col.key === 'inclusive' || col.key === 'interstate') {
    return formatBoolean(row[col.key]);
  }
  if (col.key === 'roundRule') {
    return row.roundRuleDisplay;
  }
  return formatValue(row[col.key]);
};

const TaxTab = ({ accessToken, selectedStore }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);

  const locationId = selectedStore?.id;
  const hasStore = Boolean(locationId);

  useEffect(() => {
    setData(null);
    setError('');
    setNotFound(false);
    setFetched(false);
    setLoading(false);
  }, [locationId]);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const handleFetch = useCallback(() => {
    if (!locationId) return;
    setError('');
    setNotFound(false);
    setFetched(false);
    setLoading(true);
    setIsExpanded(true);
    taxGet(accessToken, locationId)
      .then((res) => {
        setData(normaliseTaxes(res.body));
        setFetched(true);
        setLoading(false);
        return null;
      })
      .catch((err) => {
        setData(null);
        if (err?.statusCode === 404) {
          setNotFound(true);
        } else {
          setError(err?.message || 'Failed to fetch tax data.');
        }
        setFetched(true);
        setLoading(false);
      });
  }, [accessToken, locationId]);

  return (
    <section className={`sdv-card ${isExpanded ? '' : 'sdv-card-collapsed'}`}>
      <header className="sdv-card-header">
        <button
          aria-expanded={isExpanded}
          className="sdv-card-toggle"
          type="button"
          onClick={toggleExpanded}
        >
          <span aria-hidden="true" className={`sdv-card-chevron ${isExpanded ? 'sdv-card-chevron-open' : ''}`}>▸</span>
          <h2 className="sdv-card-title">Tax</h2>
        </button>
        <ButtonBlack
          isDisabled={!hasStore || loading}
          label={loading ? 'Getting...' : 'Get'}
          onClick={handleFetch}
        />
      </header>
      {isExpanded && (
        <div className="sdv-card-body">
          {error && (
            <p className="sdv-status-message sdv-status-error">{error}</p>
          )}
          {loading && <Loading />}
          {!loading && fetched && notFound && (
            <p className="sdv-card-hint">No tax data found for this store.</p>
          )}
          {!loading && fetched && data && data.length === 0 && (
            <p className="sdv-card-hint">No tax rules found for this store.</p>
          )}
          {!loading && data && data.length > 0 && (
            <div className="sdv-table-wrapper">
              <p className="sdv-table-caption">{`${data.length} tax rule(s) found.`}</p>
              <table className="sdv-table">
                <thead>
                  <tr>
                    {TAX_COLUMNS.map((col) => (
                      <th key={col.key}>{col.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.map((row) => (
                    <tr key={row.rowId}>
                      {TAX_COLUMNS.map((col) => (
                        <td key={col.key}>{renderTaxCell(row, col)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

TaxTab.defaultProps = {
  selectedStore: null,
};

TaxTab.propTypes = {
  accessToken: PropTypes.string.isRequired,
  selectedStore: PropTypes.shape({
    id: PropTypes.string,
  }),
};

export default TaxTab;
